/* eslint-disable */

import PropTypes from "prop-types";
import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Home from "@arcgis/core/widgets/Home";
import BasemapToggle from "@arcgis/core/widgets/BasemapToggle";
import LayerList from "@arcgis/core/widgets/LayerList";
import Expand from "@arcgis/core/widgets/Expand";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import Basemap from "@arcgis/core/Basemap";
import TileLayer from "@arcgis/core/layers/TileLayer";
import { NotificationManager } from "react-notifications";

// import Print from "@arcgis/core/widgets/Print";
import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { useLoading, handleLoading } from "context/LoadingContext";
import { ErrorHandelFunctionApp } from "utils/ErrorHandelFunction";
// import FeatureLayer from "@arcgis/core/layers/FeatureLayer";

const mapContext = createContext();
// Setting custom name for the context which is visible on react dev tools
mapContext.displayName = "Map Context";

// reducer
function reducer(state, action) {
  switch (action.type) {
    case "INIT": {
      return { ...state, ...action.payload };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// context provider
function MapContextProvider({ children }) {
  const mapDiv = useRef(null);

  const [, loadingDispatch] = useLoading();

  const initialState = {
    graphicsLayer: null,
    graphicsLayerSelectedParcels: null,
    map: null,
    initContext: false,
    view: null,
    mapDiv,
    villages: null,
    hods: null,
    sectors: null,
    parcels: null,
    lands: null,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);
  const [state] = value;
  useEffect(() => {
    handleLoading(loadingDispatch, true);
    if (!state.initContext && state.view && state.map) {
      state.view.destroy();
      state.map.destroy();
      state.graphicsLayer.destroy();
    }
    if (!state.initContext)
      (async () => {
        /**
         * Initialize application
         */
        const initMap = new WebMap({
          portalItem: {
            id: "650ee10dc3464289baa9747f1c054f2a",
            portal: "https://sp.pdtra.gov.jo/portal",
          },
        });

        const initView = new MapView({
          container: mapDiv.current,
          map: initMap,
        });

        const tileLayer = new TileLayer({
          // URL points to a cached tiled map service hosted on ArcGIS Server
          url: "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer",
          opacity: 0,
        });

        const basemap = new Basemap({
          baseLayers: [tileLayer],
          title: "tileLayer",
          id: "tileLayer",
          thumbnailUrl:
            "https://stamen-tiles.a.ssl.fastly.net/terrain/10/177/409.png",
        });

        const sketchGraphicsLayer = new GraphicsLayer({ listMode: "hide" });

        const homeWidget = new Home({
          view: initView,
        });

        const layerList = new LayerList({
          view: initView,
        });
        // Adds widget below other elements in the top left corner of the view

        const basemapToggle = new BasemapToggle({
          view: initView, // The view that provides access to the map's "streets-vector" basemap
          nextBasemap: basemap, // Allows for toggling to the "hybrid" basemap
        });
        const layerListExpand = new Expand({
          expandIconClass: "esri-icon-layer-list", // see https://developers.arcgis.com/javascript/latest/guide/esri-icon-font/
          // expandTooltip: "Expand LayerList", // optional, defaults to "Expand" for English locale
          view: initView,
          content: layerList,
        });
        initView.when(async () => {
          try {
            // create graphic layer
            const initGraphicsLayer = new GraphicsLayer({ listMode: "hide" });
            const initGraphicsLayerSelectedParcels = new GraphicsLayer({
              listMode: "hide",
            });
            // eslint-disable-next-line no-unused-vars
            // const layer = new FeatureLayer({
            //   url: "https://sp.pdtra.gov.jo/arcgis/rest/services/PetraLayers/MapServer/2",
            //   definitionExpression: "DLS_KEY='066401800000003'",
            // });
            // initMap.add(layer);
            initMap.add(initGraphicsLayer);
            initMap.add(initGraphicsLayerSelectedParcels);
            initMap.add(sketchGraphicsLayer);

            initView.ui.add(homeWidget, "top-left");
            // const print = new Print({
            //   view: initView,
            //   // specify your own print service
            //   printServiceUrl:
            //     "https://sp.pdtra.gov.jo/arcgis/rest/services/PetraPrintTemplates/GPServer/Export%20Web%20Map",
            // });
            // initView.ui.add(print, "top-left");
            initView.ui.add(basemapToggle, "bottom-left");
            initView.ui.add(layerListExpand, {
              position: "top-left",
            });

            const sketchVM = new SketchViewModel({
              view: initView,
              layer: sketchGraphicsLayer,
            });

            // const sketch = new Sketch({
            //   layer: sketchGraphicsLayer,
            //   view: initView,
            //   // graphic will be selected as soon as it is created
            //   creationMode: "update",
            // });

            dispatch({
              type: "INIT",
              payload: {
                view: initView,
                graphicsLayer: initGraphicsLayer,
                graphicsLayerSelectedParcels: initGraphicsLayerSelectedParcels,
                sketchGraphicsLayer,
                map: initMap,
                sketchVM,
              },
            });
          } catch (error) {
            ErrorHandelFunctionApp(error)
            handleLoading(loadingDispatch, false);
          }
        });

        return () => {
          initView.destroy();
          initMap.destroy();
        };
      })();
    handleLoading(loadingDispatch, false);
    return () => {
      handleLoading(loadingDispatch, false);
    };
  }, [state.initContext]);
  return <mapContext.Provider value={value}>{children}</mapContext.Provider>;
}

MapContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// custom hook for using context
function useMap() {
  const context = useContext(mapContext);

  if (!context) {
    throw new Error("useMap should be used inside the MapContextProvider.");
  }

  return context;
}

// Context module functions

async function setView(dispatch, view) {
  dispatch({
    type: "SET_VIEW",
    payload: view,
  });
}

export { MapContextProvider, useMap, setView };
